import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../partage/api/client';
import type { ReponseAuBandeau } from '../partage/api/types';
import { PREFIXES, relireApres, type Geste } from '../partage/relectures';

/**
 * La journée du jour dans « Maintenant » et ses bandeaux (ticket 16 de retours-testeurs) : la
 * lecture, cocher une tâche, répondre à un bandeau. Chaque geste relit tout ce qu'il change
 * (`relectures.ts`) : la journée, les quatre voix de « Maintenant », l'Agenda, les feuilles et
 * les projets.
 */

export function useJourneeDuJour() {
  return useQuery({ queryKey: PREFIXES.journeeDuJour, queryFn: () => api.journeeDuJour() });
}

function useRelireApres(geste: Geste) {
  const client = useQueryClient();
  return () => relireApres(client, geste);
}

/** Cocher (« C'est fait ») ou décocher une tâche : le même geste que partout. */
export function useCocher() {
  const relire = useRelireApres('cocher');
  return useMutation({
    mutationFn: ({ id, fait }: { id: string; fait: boolean }) => api.modifier(id, { etat: fait ? 'fait' : 'a_faire' }),
    // Sans attendre la relecture : l'annonce du geste (passée à `mutate`) part avant que le
    // bandeau, relu, ne disparaisse ; un composant démonté ne reçoit plus ses rappels.
    onSettled: () => void relire(),
  });
}

/** « Garder pour aujourd'hui » depuis la carte du débloquant (chantier `revue-design`, ticket 01). */
export function useGarderPourAujourdhui() {
  const relire = useRelireApres('garderPourAujourdhui');
  return useMutation({
    mutationFn: (tache: string) => api.garderPourAujourdhui(tache),
    onSettled: () => void relire(),
  });
}

/**
 * « Plus tard aujourd'hui » de la journée en cours au téléphone (chantier
 * `telephone-journee-agenda`, ticket 04) : le serveur passe la tâche en fin de journée et refait
 * les heures (chantier nettoyage-gestes, ticket 03).
 */
export function usePlusTardAujourdhui() {
  const relire = useRelireApres('plusTardAujourdhui');
  return useMutation({
    mutationFn: (tache: string) => api.plusTardAujourdhui(tache),
    onSettled: () => void relire(),
  });
}

export function useRepondreAuBandeau() {
  const relire = useRelireApres('repondreAuBandeau');
  return useMutation({
    mutationFn: ({ jour, reponse }: { jour: string; reponse: ReponseAuBandeau }) => api.repondreAuBandeau(jour, reponse),
    onSettled: () => void relire(),
  });
}
